import { useEffect, useRef, useState } from "react";
import {
    X, FileText, Camera, BadgeCheck, AlertCircle, Upload, Download, ChevronDown,
    User, Briefcase, Shield, CreditCard, Lock, Eye
} from "lucide-react";
import api from "../../api/axios";
import { useAuth } from "../../context/AuthContext";
import ChangePasswordModal from "./ChangePasswordModal";
import SalarySlipModal from "./SalarySlipModal";

const buildBackendUrl = (path = "") => {
    if (!path || String(path).startsWith("data:") || String(path).startsWith("blob:")) return path;
    if (/^https?:\/\//i.test(path)) return path;

    const baseURL = String(api.defaults.baseURL || "").replace(/\/+$/, "");
    const normalizedPath = String(path).startsWith("/") ? path : `/${path}`;
    return `${baseURL}${normalizedPath}`;
};

const resizeAvatarFile = (file) => new Promise((resolve, reject) => {
    if (!file?.type?.startsWith("image/")) {
        reject(new Error("Please choose an image file"));
        return;
    }

    const image = new Image();
    const objectUrl = URL.createObjectURL(file);

    image.onload = () => {
        const size = 512;
        const canvas = document.createElement("canvas");
        canvas.width = size;
        canvas.height = size;

        const context = canvas.getContext("2d");
        context.fillStyle = "#ffffff";
        context.fillRect(0, 0, size, size);

        const scale = Math.max(size / image.width, size / image.height);
        const sourceWidth = size / scale;
        const sourceHeight = size / scale;
        const sourceX = (image.width - sourceWidth) / 2;
        const sourceY = (image.height - sourceHeight) / 2;

        context.drawImage(image, sourceX, sourceY, sourceWidth, sourceHeight, 0, 0, size, size);
        URL.revokeObjectURL(objectUrl);
        resolve(canvas.toDataURL("image/jpeg", 0.85));
    };

    image.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error("Could not read the selected image"));
    };

    image.src = objectUrl;
});

const formatRoleLabel = (role) => {
    const value = String(role || "").trim();
    if (!value) return "";
    return value
        .split(/\s+/)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ");
};

const EmployeeProfileModal = ({ open, onClose }) => {
    const { employee, fetchEmployee, setEmployee } = useAuth();
    const loadedEmployeeIdRef = useRef(null);
    const wasOpenRef = useRef(false);
    const [showChangePassword, setShowChangePassword] = useState(false);
    const [showSalarySlip, setShowSalarySlip] = useState(false);
    const [genderOpen, setGenderOpen] = useState(false);
    const [activeTab, setActiveTab] = useState("personal");

    const [form, setForm] = useState({
        email: "",
        phone: "",
        address: "",
        aadharNumber: "",
        dob: "",
        gender: "",
        panNumber: "",
        uanNumber: "",
        esiNumber: "",
        bio: "",
        avatar: "",
        bankDetails: {
            accountNumber: "",
            bankName: "",
            ifscCode: "",
            accountHolderName: ""
        },
        offerLetter: "",
        offerLetterName: "",
        signedOfferLetter: "",
        signedOfferLetterName: "",
        bankPassbook: "",
        bankPassbookName: "",
        aadharCard: "",
        aadharCardName: "",
        idCardPhoto: "",
        idCardPhotoName: "",
        experienceLetter: "",
        experienceLetterName: "",
        relievingLetter: "",
        relievingLetterName: "",
        internshipLetter: "",
        internshipLetterName: ""
    });

    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) {
            wasOpenRef.current = false;
            loadedEmployeeIdRef.current = null;
            return;
        }

        const employeeId = employee?._id || employee?.employeeId || "";
        const shouldInitializeForm = employee && (!wasOpenRef.current || loadedEmployeeIdRef.current !== employeeId);

        if (shouldInitializeForm) {
            setForm({
                email: employee.email || "",
                phone: employee.phone || "",
                address: employee.address || "",
                aadharNumber: employee.aadharNumber || "",
                dob: employee.dob ? new Date(employee.dob).toISOString().split('T')[0] : "",
                gender: employee.gender || "",
                panNumber: employee.panNumber || "",
                uanNumber: employee.uanNumber || "",
                esiNumber: employee.esiNumber || "",
                bio: employee.bio || "",
                avatar: employee.avatar || "",
                bankDetails: {
                    accountNumber: employee.bankDetails?.accountNumber || "",
                    bankName: employee.bankDetails?.bankName || "",
                    ifscCode: employee.bankDetails?.ifscCode || "",
                    accountHolderName: employee.bankDetails?.accountHolderName || ""
                },
                offerLetter: employee.offerLetter || "",
                offerLetterName: employee.offerLetterName || "",
                signedOfferLetter: employee.signedOfferLetter || "",
                signedOfferLetterName: employee.signedOfferLetterName || "",
                bankPassbook: employee.bankPassbook || "",
                bankPassbookName: employee.bankPassbookName || "",
                aadharCard: employee.aadharCard || "",
                aadharCardName: employee.aadharCardName || "",
                idCardPhoto: employee.idCardPhoto || "",
                idCardPhotoName: employee.idCardPhotoName || "",
                experienceLetter: employee.experienceLetter || "",
                experienceLetterName: employee.experienceLetterName || "",
                relievingLetter: employee.relievingLetter || "",
                relievingLetterName: employee.relievingLetterName || "",
                internshipLetter: employee.internshipLetter || "",
                internshipLetterName: employee.internshipLetterName || ""
            });
            loadedEmployeeIdRef.current = employeeId;
        }

        wasOpenRef.current = true;
    }, [employee, open]);

    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === "Escape") onClose();
        };
        if (open) window.addEventListener("keydown", handleEsc);
        return () => window.removeEventListener("keydown", handleEsc);
    }, [open, onClose]);

    const handleSave = async () => {
        setSaving(true);
        try {
            const res = await api.put("/api/employee/profile/me", form);
            if (res.data) {
                setEmployee(res.data);
            }
            await fetchEmployee();
            onClose();
        } catch (err) {
            alert(err.response?.data?.message || "Profile update failed");
        } finally {
            setSaving(false);
        }
    };

    const handleAvatarChange = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (!file) return;

        try {
            const avatarDataUrl = await resizeAvatarFile(file);
            setForm((currentForm) => ({ ...currentForm, avatar: avatarDataUrl }));
        } catch (err) {
            alert(err.message || "Profile photo upload failed");
        }
    };

    const getProfileStats = () => {
        const requiredCore = ['email', 'phone', 'address', 'aadharNumber', 'dob', 'gender', 'panNumber', 'accountNumber'];
        const optionalDocs = ['avatar', 'signedOfferLetter', 'bankPassbook', 'aadharCard', 'idCardPhoto'];

        const getValue = (field) => {
            if (field === 'accountNumber') return form.bankDetails?.accountNumber || employee.bankDetails?.accountNumber;
            if (field === 'signedOfferLetter') return form.signedOfferLetter || employee.signedOfferLetter;
            if (field === 'bankPassbook') return form.bankPassbook || employee.bankPassbook;
            if (field === 'aadharCard') return form.aadharCard || employee.aadharCard;
            if (field === 'idCardPhoto') return form.idCardPhoto || employee.idCardPhoto;
            return form?.[field] || employee?.[field];
        };

        const completedCore = requiredCore.filter(field => Boolean(getValue(field)));
        const completedDocs = optionalDocs.filter(field => Boolean(getValue(field)));

        const isCoreComplete = completedCore.length === requiredCore.length;
        const rawPercent = Math.round(((completedCore.length + completedDocs.length) / (requiredCore.length + optionalDocs.length)) * 100);
        const percent = isCoreComplete ? Math.max(rawPercent, 100) : rawPercent;

        return {
            percent,
            completed: isCoreComplete
        };
    };

    if (!open) return null;

    const { percent, completed } = getProfileStats();

    // Standard Styles
    const inputStyle = "w-full px-4 py-2.5 bg-neutral-50 dark:bg-neutral-800/30 border border-neutral-200 dark:border-neutral-700 rounded-xl focus:ring-2 focus:ring-[var(--accent-primary)] focus:border-transparent text-sm transition-all text-neutral-900 dark:text-neutral-50 outline-none";
    const disabledStyle = "w-full px-4 py-2.5 bg-neutral-100/50 dark:bg-neutral-900/50 border border-neutral-200 dark:border-neutral-800 rounded-xl text-sm text-neutral-400 dark:text-neutral-500 cursor-not-allowed select-none outline-none";
    const generatedDocuments = [
        {
            label: "Offer Letter",
            url: buildBackendUrl(form.offerLetter),
            fileName: form.offerLetterName || "Offer_Letter.pdf",
            emptyText: "Admin has not generated this letter yet.",
            hideIfEmpty: false
        },
        {
            label: "Internship Letter",
            url: buildBackendUrl(form.internshipLetter),
            fileName: form.internshipLetterName || "Internship_Letter.pdf",
            emptyText: "Admin has not generated this letter yet.",
            hideIfEmpty: true
        },
        {
            label: "Experience Letter",
            url: buildBackendUrl(form.experienceLetter),
            fileName: form.experienceLetterName || "Experience_Letter.pdf",
            emptyText: "Admin has not generated this letter yet.",
            hideIfEmpty: true
        },
        {
            label: "Relieving Letter",
            url: buildBackendUrl(form.relievingLetter),
            fileName: form.relievingLetterName || "Relieving_Letter.pdf",
            emptyText: "Admin has not generated this letter yet.",
            hideIfEmpty: true
        }
    ];
    const employeeUploadedDocs = [
        {
            key: "signedOfferLetter",
            label: "Signed Offer Letter *",
            value: buildBackendUrl(form.signedOfferLetter),
            name: form.signedOfferLetterName,
            defaultName: "Signed_Offer_Letter.pdf",
            placeholder: "Please upload your signed offer letter (PDF or Image).",
            accept: "application/pdf,image/*"
        },
        {
            key: "bankPassbook",
            label: "Bank Passbook Photo *",
            value: buildBackendUrl(form.bankPassbook),
            name: form.bankPassbookName,
            defaultName: "Bank_Passbook.pdf",
            placeholder: "Please upload your Bank Passbook Photo (PDF or Image).",
            accept: "application/pdf,image/*"
        },
        {
            key: "aadharCard",
            label: "Aadhar Card Upload *",
            value: buildBackendUrl(form.aadharCard),
            name: form.aadharCardName,
            defaultName: "Aadhar_Card.pdf",
            placeholder: "Please upload your Aadhar Card (PDF or Image).",
            accept: "application/pdf,image/*"
        },
        {
            key: "idCardPhoto",
            label: "ID Card Photo *",
            value: buildBackendUrl(form.idCardPhoto),
            name: form.idCardPhotoName,
            defaultName: "ID_Card_Photo.png",
            placeholder: "Please upload your ID Card Photo (Image).",
            accept: "image/*"
        }
    ];

    const tabs = [
        { id: "personal", label: "Personal Info", icon: User },
        { id: "workspace", label: "Workspace & Job", icon: Briefcase },
        { id: "identity", label: "Identity & Regulatory", icon: Shield },
        { id: "bank", label: "Bank Account", icon: CreditCard },
        { id: "documents", label: "Documents & Files", icon: FileText }
    ];

    const radius = 26;
    const circumference = 2 * Math.PI * radius;
    const strokeDashoffset = circumference - (percent / 100) * circumference;

    const ReadOnlyField = ({ label, value }) => (
        <Field label={label}>
            <div className="relative">
                <input disabled value={value || ""} className={`${disabledStyle} pr-10`} />
                <Lock size={13} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-neutral-400 dark:text-neutral-500" />
            </div>
        </Field>
    );

    return (
        <>
            <div className="fixed inset-0 z-[100] flex justify-center items-center px-4 py-6">
                <div onClick={onClose} className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

                {/* MODAL BODY */}
                <div className="relative w-full max-w-5xl h-[680px] max-h-[90vh] rounded-3xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-[#121214] shadow-2xl transition-all flex flex-col overflow-hidden">

                    {/* MODAL HEADER */}
                    <div className="flex justify-between items-center px-6 sm:px-8 py-4 border-b border-neutral-100 dark:border-neutral-800 flex-shrink-0">
                        <div>
                            <h2 className="text-xl font-bold text-neutral-900 dark:text-neutral-50">Profile Settings</h2>
                            <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-0.5">Manage your digital workspace identity, credentials, and records.</p>
                        </div>
                        <button onClick={onClose} className="w-8 h-8 rounded-lg bg-neutral-50 dark:bg-neutral-800 hover:bg-neutral-100 dark:hover:bg-neutral-700 flex items-center justify-center transition-all">
                            <X size={16} className="text-neutral-500 dark:text-neutral-400" />
                        </button>
                    </div>

                    {/* MAIN TWO-COLUMN SPLIT PANEL */}
                    <div className="flex flex-col md:flex-row flex-1 overflow-hidden">

                        {/* LEFT: SIDEBAR TAB NAVIGATOR */}
                        <div className="w-full md:w-64 border-b md:border-b-0 md:border-r border-neutral-100 dark:border-neutral-800 bg-neutral-50/20 dark:bg-neutral-900/10 flex-shrink-0 flex flex-col overflow-y-auto p-5">
                            
                            {/* User Profile Info Card */}
                            <div className="flex flex-col items-center text-center pb-5 border-b border-neutral-100 dark:border-neutral-800 mb-5">

                                <h3 className="font-semibold text-neutral-900 dark:text-neutral-50 text-sm">{employee?.name}</h3>
                                <p className="text-[11px] text-neutral-400 dark:text-neutral-500 mt-0.5">{employee?.designation || formatRoleLabel(employee?.role) || "Employee"}</p>
                            </div>

                            {/* Navigation Tabs */}
                            <div className="flex md:flex-col gap-1 overflow-x-auto md:overflow-x-visible pb-3 md:pb-0 scrollbar-none">
                                {tabs.map((tab) => {
                                    const IconComponent = tab.icon;
                                    return (
                                        <button
                                            key={tab.id}
                                            type="button"
                                            onClick={() => setActiveTab(tab.id)}
                                            className={`flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all whitespace-nowrap ${
                                                activeTab === tab.id
                                                    ? "bg-[var(--accent-primary)] text-white shadow-md shadow-[var(--accent-primary)]/10"
                                                    : "text-neutral-500 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-neutral-100 hover:bg-neutral-100/60 dark:hover:bg-neutral-800/40"
                                            }`}
                                        >
                                            <IconComponent size={16} />
                                            <span>{tab.label}</span>
                                        </button>
                                    );
                                })}
                            </div>

                            {/* Profile Completion SVG */}
                            <div className="hidden md:flex flex-col items-center gap-2 mt-auto pt-5 border-t border-neutral-100 dark:border-neutral-800">
                                <div className="relative flex items-center justify-center w-14 h-14">
                                    <svg className="w-full h-full transform -rotate-90">
                                        <circle
                                            cx="28"
                                            cy="28"
                                            r={radius}
                                            className="stroke-neutral-100 dark:stroke-neutral-800/60"
                                            strokeWidth="4"
                                            fill="transparent"
                                        />
                                        <circle
                                            cx="28"
                                            cy="28"
                                            r={radius}
                                            className={`transition-all duration-500 ${completed ? 'stroke-emerald-500' : 'stroke-[var(--accent-primary)]'}`}
                                            strokeWidth="4"
                                            fill="transparent"
                                            strokeDasharray={circumference}
                                            strokeDashoffset={strokeDashoffset}
                                            strokeLinecap="round"
                                        />
                                    </svg>
                                    <span className="absolute text-xs font-bold text-neutral-800 dark:text-neutral-200">{percent}%</span>
                                </div>
                                <div className="text-center">
                                    <span className="text-[10px] font-bold text-neutral-400 dark:text-neutral-500 uppercase tracking-wider">Profile Status</span>
                                    <div className="flex items-center gap-1 justify-center mt-0.5">
                                        {completed ? (
                                            <span className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 flex items-center gap-0.5">
                                                <BadgeCheck size={11} /> Verified
                                            </span>
                                        ) : (
                                            <span className="text-[10px] font-bold text-amber-600 dark:text-amber-400 flex items-center gap-0.5 animate-pulse">
                                                <AlertCircle size={11} /> Incomplete
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>

                        </div>

                        {/* RIGHT: TAB CONTENT PANEL */}
                        <div className="flex-1 overflow-y-auto p-6 sm:p-8">

                            {/* Tab 1: Personal Details */}
                            {activeTab === "personal" && (
                                <div className="space-y-6">
                                    <div>
                                        <h3 className="text-base font-bold text-neutral-900 dark:text-neutral-50">Personal Profile</h3>
                                        <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-0.5">Update your birth details, gender identity, and location information.</p>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <Field label="Date of Birth *">
                                            <input type="date" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} className={inputStyle} />
                                        </Field>
                                        <Field label="Gender *">
                                            <div className="relative">
                                                <button
                                                    type="button"
                                                    onClick={() => setGenderOpen(!genderOpen)}
                                                    className={`${inputStyle} flex items-center justify-between text-left pr-10 ${
                                                        genderOpen ? 'border-[var(--accent-primary)] ring-2 ring-violet-500/20' : ''
                                                    }`}
                                                >
                                                    <span>{form.gender || "Select Gender"}</span>
                                                    <ChevronDown size={16} className={`text-neutral-400 transition-transform ${genderOpen ? 'rotate-180 text-[var(--accent-primary)]' : ''}`} />
                                                </button>
                                                
                                                {genderOpen && (
                                                    <>
                                                        <div className="fixed inset-0 z-40" onClick={() => setGenderOpen(false)} />
                                                        <div className="absolute left-0 right-0 mt-2 z-50 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-2xl shadow-xl overflow-hidden py-1">
                                                            {[
                                                                { value: "", label: "Select Gender" },
                                                                { value: "Male", label: "Male" },
                                                                { value: "Female", label: "Female" },
                                                                { value: "Other", label: "Other" }
                                                            ].map((opt) => (
                                                                <button
                                                                    key={opt.value}
                                                                    type="button"
                                                                    onClick={() => {
                                                                        setForm({ ...form, gender: opt.value });
                                                                        setGenderOpen(false);
                                                                    }}
                                                                    className={`w-full text-left px-4 py-2.5 text-sm transition-all ${
                                                                        form.gender === opt.value
                                                                            ? "bg-violet-50 dark:bg-violet-950/20 text-[var(--accent-primary)] dark:text-violet-400 font-semibold"
                                                                            : "text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800/50 hover:text-neutral-900 dark:hover:text-neutral-50"
                                                                    }`}
                                                                >
                                                                    {opt.label}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        </Field>
                                    </div>
                                    <div className="flex flex-col gap-4">
                                        <Field label="Permanent Address *">
                                            <textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className={inputStyle} placeholder="Your full permanent address" />
                                        </Field>
                                        <Field label="Bio / Notes">
                                            <textarea rows={2} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} className={inputStyle} placeholder="Brief bio or notes..." />
                                        </Field>
                                    </div>
                                </div>
                            )}

                            {/* Tab 2: Workspace & Job */}
                            {activeTab === "workspace" && (
                                <div className="space-y-6">
                                    <div>
                                        <div className="flex items-center gap-1.5">
                                            <h3 className="text-base font-bold text-neutral-900 dark:text-neutral-50">Workspace Details</h3>
                                            <Lock size={13} className="text-neutral-400 mt-0.5" />
                                        </div>
                                        <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-0.5">Your official corporate details. These fields are read-only and configured by HR.</p>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <ReadOnlyField label="Employee ID" value={employee?.employeeId} />
                                        <ReadOnlyField label="Team / Department" value={employee?.team} />
                                        <ReadOnlyField label="Role / Designation" value={formatRoleLabel(employee?.role) || employee?.designation} />
                                        <ReadOnlyField label="Work Location" value={employee?.workLocation || "Head Office"} />
                                        <ReadOnlyField label="Employment Type" value={employee?.employmentType || "Full-time"} />
                                        <ReadOnlyField label="Joining Date" value={employee?.joiningDate ? new Date(employee?.joiningDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : ""} />
                                        <ReadOnlyField label="Shift Timing" value={employee?.shiftTiming || "09:00 AM - 06:00 PM"} />
                                        <ReadOnlyField label="Working Days" value={formatWorkingDays(employee?.workingDays || "Monday - Friday")} />
                                        <ReadOnlyField label="Employee Status" value={employee?.employeeStatus || "Active"} />
                                    </div>
                                    <div>
                                        <h4 className="text-xs font-bold text-neutral-500 uppercase">Assigned Assets</h4>
                                        <div className="mt-2 space-y-2">
                                            {(employee?.assignedAssets || []).length ? employee.assignedAssets.map((asset, index) => (
                                                <div key={`${asset.assetName}-${index}`} className="rounded-xl border border-neutral-200 bg-neutral-50 px-4 py-3 text-sm dark:border-neutral-800 dark:bg-neutral-900/40">
                                                    <p className="font-semibold text-neutral-900 dark:text-neutral-100">{asset.assetName || "Assigned asset"}</p>
                                                    <p className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400">{asset.description || "Assigned"}</p>
                                                </div>
                                            )) : <p className="rounded-xl border border-dashed border-neutral-200 px-4 py-3 text-sm text-neutral-500 dark:border-neutral-800">No assets assigned.</p>}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Tab 3: Contact & Identity */}
                            {activeTab === "identity" && (
                                <div className="space-y-6">
                                    <div>
                                        <h3 className="text-base font-bold text-neutral-900 dark:text-neutral-50">Identity & Contact Info</h3>
                                        <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-0.5">Manage your contact credentials and government regulatory IDs.</p>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <Field label="Email Address *">
                                            <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputStyle} />
                                        </Field>
                                        <Field label="Phone Number *">
                                            <input type="text" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputStyle} />
                                        </Field>
                                        <Field label="PAN Card Number *">
                                            <input type="text" value={form.panNumber} onChange={(e) => setForm({ ...form, panNumber: e.target.value })} className={inputStyle} />
                                        </Field>
                                        <Field label="Aadhar Card Number *">
                                            <input type="text" value={form.aadharNumber} onChange={(e) => setForm({ ...form, aadharNumber: e.target.value })} className={inputStyle} />
                                        </Field>
                                        <Field label="UAN Number (Optional)">
                                            <input type="text" value={form.uanNumber} onChange={(e) => setForm({ ...form, uanNumber: e.target.value })} className={inputStyle} />
                                        </Field>
                                        <Field label="ESI Number (Optional)">
                                            <input type="text" value={form.esiNumber} onChange={(e) => setForm({ ...form, esiNumber: e.target.value })} className={inputStyle} />
                                        </Field>
                                    </div>
                                </div>
                            )}

                            {/* Tab 4: Bank Details */}
                            {activeTab === "bank" && (
                                <div className="space-y-6">
                                    <div>
                                        <h3 className="text-base font-bold text-neutral-900 dark:text-neutral-50">Bank Details</h3>
                                        <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-0.5">Manage your receiving bank account details for payroll and reimbursement.</p>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <Field label="Account Holder Name *">
                                            <input
                                                type="text"
                                                value={form.bankDetails?.accountHolderName || ""}
                                                onChange={(e) => setForm({
                                                    ...form,
                                                    bankDetails: { ...form.bankDetails, accountHolderName: e.target.value }
                                                })}
                                                className={inputStyle}
                                                placeholder="As per bank passbook"
                                            />
                                        </Field>
                                        <Field label="Bank Name *">
                                            <input
                                                type="text"
                                                value={form.bankDetails?.bankName || ""}
                                                onChange={(e) => setForm({
                                                    ...form,
                                                    bankDetails: { ...form.bankDetails, bankName: e.target.value }
                                                })}
                                                className={inputStyle}
                                                placeholder="e.g. HDFC Bank, ICICI Bank"
                                            />
                                        </Field>
                                        <Field label="Account Number *">
                                            <input
                                                type="text"
                                                value={form.bankDetails?.accountNumber || ""}
                                                onChange={(e) => setForm({
                                                    ...form,
                                                    bankDetails: { ...form.bankDetails, accountNumber: e.target.value }
                                                })}
                                                className={inputStyle}
                                                placeholder="Enter account number"
                                            />
                                        </Field>
                                        <Field label="IFSC Code *">
                                            <input
                                                type="text"
                                                value={form.bankDetails?.ifscCode || ""}
                                                onChange={(e) => setForm({
                                                    ...form,
                                                    bankDetails: { ...form.bankDetails, ifscCode: e.target.value.toUpperCase() }
                                                })}
                                                className={inputStyle}
                                                placeholder="e.g. HDFC0001234"
                                            />
                                        </Field>
                                    </div>
                                </div>
                            )}

                            {/* Tab 5: Documents */}
                            {activeTab === "documents" && (
                                <div className="space-y-6">
                                    <div>
                                        <h3 className="text-base font-bold text-neutral-900 dark:text-neutral-50">Corporate & Identification Documents</h3>
                                        <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-0.5">View documents generated by your employer and manage uploaded verification cards.</p>
                                    </div>

                                    {/* Admin Generated Letters */}
                                    <div className="space-y-3">
                                        <h4 className="text-[10px] font-bold text-neutral-400 dark:text-neutral-500 uppercase tracking-widest">Employer Generated Letters</h4>
                                        <div className="flex flex-col gap-3">
                                            {generatedDocuments
                                                .filter((doc) => !doc.hideIfEmpty || doc.url)
                                                .map((doc) => (
                                                    <div key={doc.label} className="p-4 rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-neutral-50/50 dark:bg-neutral-900/30 flex flex-col md:flex-row md:items-center justify-between gap-4">
                                                        <div className="flex items-start gap-3">
                                                            <div className="w-10 h-10 rounded-xl bg-[var(--accent-primary)]/10 text-[var(--accent-primary)] flex items-center justify-center flex-shrink-0">
                                                                <FileText size={20} />
                                                            </div>
                                                            <div>
                                                                <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-50">{doc.label}</p>
                                                                {doc.url ? (
                                                                    <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-0.5 flex items-center gap-1 font-medium">
                                                                        Generated: {doc.fileName}
                                                                    </p>
                                                                ) : (
                                                                    <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-0.5">
                                                                        {doc.emptyText}
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </div>
                                                        {doc.url && (
                                                            <div className="flex flex-wrap items-center gap-2">
                                                                <a
                                                                    href={doc.url}
                                                                    target="_blank"
                                                                    rel="noreferrer"
                                                                    className="px-4 py-2 text-xs font-semibold text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-xl transition-all flex items-center gap-1.5"
                                                                >
                                                                    <Eye size={13} />
                                                                    View
                                                                </a>
                                                                <a
                                                                    href={doc.url}
                                                                    download={doc.fileName}
                                                                    className="px-4 py-2 text-xs font-semibold text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-xl transition-all flex items-center gap-1.5"
                                                                >
                                                                    <Download size={13} />
                                                                    Download
                                                                </a>
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                        </div>
                                    </div>

                                    {/* Employee Uploaded Verification Docs */}
                                    <div className="space-y-3 pt-3 border-t border-neutral-100 dark:border-neutral-800">
                                        <h4 className="text-[10px] font-bold text-neutral-400 dark:text-neutral-500 uppercase tracking-widest">Mandatory Verification Uploads</h4>
                                        <div className="flex flex-col gap-3">
                                            {employeeUploadedDocs.map((doc) => {
                                                const isUploaded = Boolean(doc.value);
                                                return (
                                                    <div
                                                        key={doc.key}
                                                        className={`p-4 rounded-2xl border transition-all flex flex-col md:flex-row md:items-center justify-between gap-4 ${
                                                            isUploaded
                                                                ? "border-emerald-500/20 bg-emerald-500/[0.02] dark:border-emerald-500/30 dark:bg-emerald-500/[0.02]"
                                                                : "border-neutral-200 dark:border-neutral-800 bg-neutral-50/50 dark:bg-neutral-900/30"
                                                        }`}
                                                    >
                                                        <div className="flex items-start gap-3">
                                                            <div
                                                                className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                                                                    isUploaded
                                                                        ? "bg-emerald-500/10 text-emerald-500 dark:bg-emerald-500/25"
                                                                        : "bg-[var(--accent-primary)]/10 text-[var(--accent-primary)]"
                                                                }`}
                                                            >
                                                                {isUploaded ? <BadgeCheck size={20} /> : <FileText size={20} />}
                                                            </div>
                                                            <div>
                                                                <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-50">{doc.label}</p>
                                                                {isUploaded ? (
                                                                    <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-0.5 font-medium">
                                                                        Uploaded: {doc.name || doc.defaultName}
                                                                    </p>
                                                                ) : (
                                                                    <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-0.5">
                                                                        {doc.placeholder}
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-3">
                                                            {isUploaded && (
                                                                <>
                                                                    <a
                                                                        href={doc.value}
                                                                        target="_blank"
                                                                        rel="noreferrer"
                                                                        className="px-4 py-2 text-xs font-semibold text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-xl transition-all flex items-center gap-1.5"
                                                                    >
                                                                        <Eye size={13} />
                                                                        View
                                                                    </a>
                                                                    <a
                                                                        href={doc.value}
                                                                        download={doc.name || doc.defaultName}
                                                                        className="px-4 py-2 text-xs font-semibold text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-xl transition-all flex items-center gap-1.5"
                                                                    >
                                                                        <Download size={13} />
                                                                        Download
                                                                    </a>
                                                                </>
                                                            )}
                                                            <label className="px-4 py-2 text-xs font-semibold text-white bg-[var(--accent-primary)] hover:bg-[var(--accent-hover)] rounded-xl cursor-pointer transition-all shadow-sm flex items-center gap-1.5">
                                                                <Upload size={13} />
                                                                {isUploaded ? "Replace" : "Upload"}
                                                                <input
                                                                    type="file"
                                                                    accept={doc.accept}
                                                                    className="hidden"
                                                                    onChange={(e) => {
                                                                        const file = e.target.files[0];
                                                                        if (file) {
                                                                            const reader = new FileReader();
                                                                            reader.onloadend = () => {
                                                                                setForm({
                                                                                    ...form,
                                                                                    [doc.key]: reader.result,
                                                                                    [`${doc.key}Name`]: file.name
                                                                                });
                                                                            };
                                                                            reader.readAsDataURL(file);
                                                                        }
                                                                    }}
                                                                />
                                                            </label>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            )}

                        </div>

                    </div>

                    {/* MODAL FOOTER */}
                    <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3 px-6 sm:px-8 py-4 border-t border-neutral-100 dark:border-neutral-800 bg-neutral-50/50 dark:bg-neutral-900/30 flex-shrink-0">
                        <div className="flex items-center gap-2">
                            <button onClick={() => setShowChangePassword(true)} className="w-full sm:w-auto px-4 py-2 text-xs font-semibold text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-xl transition-all text-center">
                                Change Password
                            </button>

                            <button onClick={() => setShowSalarySlip(true)} className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 px-4 py-2 text-xs font-semibold text-[var(--accent-primary)] hover:bg-[var(--accent-primary)]/10 border border-[var(--accent-primary)]/20 rounded-xl transition-all text-center">
                                <FileText size={14} />
                                Salary Slip
                            </button>
                        </div>

                        <div className="flex items-center gap-2">
                            <button onClick={onClose} className="w-full sm:w-auto px-4 py-2 text-xs font-semibold text-neutral-500 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-xl transition-all text-center">
                                Close
                            </button>

                            <button onClick={handleSave} disabled={saving} className="w-full sm:w-auto px-5 py-2 text-xs font-semibold text-white bg-[var(--accent-primary)] hover:bg-[var(--accent-hover)] rounded-xl transition-all shadow-sm text-center">
                                {saving ? "Saving..." : "Save Changes"}
                            </button>
                        </div>
                    </div>

                </div>
            </div>

            <ChangePasswordModal open={showChangePassword} onClose={() => setShowChangePassword(false)} onSuccess={() => { localStorage.clear(); window.location.href = "/login"; }} />
            <SalarySlipModal open={showSalarySlip} onClose={() => setShowSalarySlip(false)} />
        </>
    );
};

const formatWorkingDays = (workingDaysInput) => {
    if (!workingDaysInput) return "";

    let daysArray = [];
    if (Array.isArray(workingDaysInput)) {
        daysArray = workingDaysInput.map(d => String(d).trim()).filter(Boolean);
    } else if (typeof workingDaysInput === "string") {
        const str = workingDaysInput.trim();
        if (!str) return "";
        if (str.includes("-")) return str;
        daysArray = str.split(",").map(d => d.trim()).filter(Boolean);
    } else {
        daysArray = [String(workingDaysInput).trim()].filter(Boolean);
    }

    if (daysArray.length === 0) return "";
    if (daysArray.length === 1) {
        const d = daysArray[0];
        return d.charAt(0).toUpperCase() + d.slice(1).toLowerCase();
    }

    const weekdayOrder = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
    const weekdayShortMap = {
        mon: "Monday", monday: "Monday",
        tue: "Tuesday", tues: "Tuesday", tuesday: "Tuesday",
        wed: "Wednesday", wednesday: "Wednesday",
        thu: "Thursday", thur: "Thursday", thurs: "Thursday", thursday: "Thursday",
        fri: "Friday", friday: "Friday",
        sat: "Saturday", saturday: "Saturday",
        sun: "Sunday", sunday: "Sunday"
    };

    const normalizedDays = daysArray.map(d => {
        const lower = d.toLowerCase();
        if (weekdayShortMap[lower]) return weekdayShortMap[lower];
        return d.charAt(0).toUpperCase() + d.slice(1).toLowerCase();
    });

    const indices = normalizedDays.map(d => weekdayOrder.indexOf(d)).filter(idx => idx !== -1);
    if (indices.length <= 1) return normalizedDays.join(", ");

    const sortedIndices = [...indices].sort((a, b) => a - b);
    let isConsecutive = true;
    for (let i = 1; i < sortedIndices.length; i++) {
        if (sortedIndices[i] !== sortedIndices[i - 1] + 1) {
            isConsecutive = false;
            break;
        }
    }

    if (isConsecutive && sortedIndices.length > 1) {
        const startDay = weekdayOrder[sortedIndices[0]];
        const endDay = weekdayOrder[sortedIndices[sortedIndices.length - 1]];
        return `${startDay} - ${endDay}`;
    }

    return normalizedDays.join(", ");
};

const Field = ({ label, children }) => {
    return (
        <div className="flex flex-col gap-1.5">
            <label className="text-[11px] font-semibold text-neutral-400 dark:text-neutral-500 tracking-wide">{label}</label>
            {children}
        </div>
    );
};

export default EmployeeProfileModal;
